import React, { useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { cn } from './ui/utils';
import { BETA_FEEDBACK_RATING_LABELS, BetaFeedbackRating } from './betaFeedback';

const DEFAULT_BETA_TOOLTIP =
  'Your school was chosen to try this new experience before everyone else! Click to give feedback.';

interface BetaLabelProps {
  children?: React.ReactNode;
  tooltipContent?: React.ReactNode;
  className?: string;
  /** Rotation of the badge: 'right' (clockwise, default) or 'left' (counter-clockwise) */
  rotation?: 'left' | 'right';
  rating: BetaFeedbackRating | null;
  feedbackText: string;
  onRatingChange: (rating: BetaFeedbackRating) => void;
  onFeedbackTextChange: (value: string) => void;
}

export function BetaLabel({
  children,
  tooltipContent,
  className,
  rotation = 'right',
  rating,
  feedbackText,
  onRatingChange,
  onFeedbackTextChange,
}: BetaLabelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const rotationClass = rotation === 'left' ? '-tw-rotate-12' : 'tw-rotate-12';

  const options: Array<{
    rating: BetaFeedbackRating;
    label: string;
    icon: React.ReactNode;
  }> = [
    {
      rating: 'dont-like-it',
      label: BETA_FEEDBACK_RATING_LABELS['dont-like-it'],
      icon: <ThumbsDown className="tw-h-6 tw-w-6" aria-hidden="true" />,
    },
    {
      rating: 'like-it',
      label: BETA_FEEDBACK_RATING_LABELS['like-it'],
      icon: <ThumbsUp className="tw-h-6 tw-w-6" aria-hidden="true" />,
    },
    {
      rating: 'love-it',
      label: BETA_FEEDBACK_RATING_LABELS['love-it'],
      icon: (
        <span className="tw-flex tw-items-center tw-justify-center tw-gap-1" aria-hidden="true">
          <ThumbsUp className="tw-h-6 tw-w-6" />
          <ThumbsUp className="tw-h-6 tw-w-6" />
        </span>
      ),
    },
  ];

  const dialogContent = (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className="tw-max-w-xl tw-space-y-4" style={{ padding: '24px' }}>
        <DialogTitle>Share beta feedback</DialogTitle>
        <DialogDescription>Tell us what you think about this experience.</DialogDescription>
        <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-3">
          {options.map(option => {
            const selected = rating === option.rating;
            return (
              <button
                key={option.rating}
                type="button"
                onClick={() => onRatingChange(option.rating)}
                className={cn(
                  'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-p-3 tw-transition-colors',
                  selected
                    ? 'tw-border-blue-500 tw-bg-blue-50 tw-text-blue-700'
                    : 'tw-border-neutral-200 tw-bg-white hover:tw-border-neutral-300',
                )}
                aria-pressed={selected}
              >
                {option.icon}
                <span className="tw-text-sm tw-font-medium">{option.label}</span>
              </button>
            );
          })}
        </div>
        <div className="tw-space-y-2">
          <label htmlFor="beta-feedback-text" className="tw-block tw-text-sm tw-font-medium">
            General feedback
          </label>
          <textarea
            id="beta-feedback-text"
            value={feedbackText}
            onChange={event => onFeedbackTextChange(event.target.value)}
            className="tw-min-h-28 tw-w-full tw-resize-y tw-rounded-md tw-border tw-border-neutral-300 tw-p-3 tw-text-sm focus:tw-border-blue-500 focus:tw-outline-hidden"
            placeholder="What should we improve?"
          />
        </div>
      </DialogContent>
    </Dialog>
  );

  const inlineButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Beta label"
          onClick={() => setIsModalOpen(true)}
          className={cn(
            'tw-rounded-full tw-border tw-border-yellow-600 tw-bg-yellow-400 tw-px-2 tw-py-1 tw-text-xs tw-font-bold tw-text-yellow-900 tw-shadow-md tw-shadow-yellow-600 tw-cursor-pointer tw-shrink-0 tw-flex tw-items-center tw-justify-center',
            rotationClass,
          )}
        >
          Beta!
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" align="start" sideOffset={10} className="tw-max-w-xs">
        <span>{tooltipContent ?? DEFAULT_BETA_TOOLTIP}</span>
      </TooltipContent>
    </Tooltip>
  );

  const floatingButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Beta label"
          onClick={() => setIsModalOpen(true)}
          className={cn(
            'tw-absolute -tw-left-3 -tw-top-2 tw-z-[30] tw-rounded-full tw-border tw-border-yellow-600 tw-bg-yellow-400 tw-px-2 tw-py-1 tw-text-xs tw-font-bold tw-text-yellow-900 tw-shadow-md tw-shadow-yellow-600 tw-cursor-pointer',
            rotationClass,
          )}
        >
          Beta!
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" align="start" sideOffset={10} className="tw-max-w-xs">
        <span>{tooltipContent ?? DEFAULT_BETA_TOOLTIP}</span>
      </TooltipContent>
    </Tooltip>
  );

  if (children === undefined) {
    return (
      <>
        {inlineButton}
        {dialogContent}
      </>
    );
  }

  return (
    <div className={cn('tw-relative', className)}>
      {children}
      {floatingButton}
      {dialogContent}
    </div>
  );
}
